import { useId, useState } from 'react';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Textarea } from '~/components/ui/textarea';

import { useAcceptPostStepProducts } from '../../hooks/use-post-step-retry';
import type { PostStepAcceptClass, PostStepName } from '../../types/post-step-retry.api.types';

import { formatCount } from './batch-retry-utils';
import { STEP_NAME } from './post-step-journey-utils';

/** What to accept: some products by id, or every given-up product of the step. */
export interface PostStepAcceptTarget {
  step: PostStepName;
  productIds?: string[];
  productClass?: PostStepAcceptClass;
  gaveUpOnly?: boolean;
  /** How many products the target covers, for the copy. */
  count: number;
}

export interface PostStepAcceptDialogProps {
  jobId: string;
  /** Null = closed. */
  target: PostStepAcceptTarget | null;
  onClose: () => void;
}

const MAX_REASON = 1000;

/**
 * "Accept as is": the products ship with what they have and every retry skips them until undone.
 * A reason is required and recorded with the caller.
 */
export function PostStepAcceptDialog({ jobId, target, onClose }: PostStepAcceptDialogProps) {
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {target && <AcceptBody jobId={jobId} target={target} onClose={onClose} />}
    </Dialog>
  );
}

function AcceptBody({
  jobId,
  target,
  onClose,
}: {
  jobId: string;
  target: PostStepAcceptTarget;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const { mutate: accept, isPending } = useAcceptPostStepProducts(jobId);
  const trimmed = reason.trim();
  const noun = target.count === 1 ? 'product' : 'products';

  return (
    <DialogContent
      data-testid="post-step-accept-dialog"
      className="max-w-lg"
      onInteractOutside={(e) => {
        if (isPending) e.preventDefault();
      }}
      onEscapeKeyDown={(e) => {
        if (isPending) e.preventDefault();
      }}
    >
      <DialogHeader>
        <DialogTitle className="text-lg font-semibold">
          Accept {formatCount(target.count)} {noun} as is
        </DialogTitle>
        <DialogDescription>
          They keep the {STEP_NAME[target.step].toLowerCase()} they have now and retries skip them.
          Undo any time from the product row.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1.5">
        <label htmlFor={reasonId} className="text-sm font-semibold">
          Why
        </label>
        <Textarea
          id={reasonId}
          data-testid="post-step-accept-reason"
          value={reason}
          maxLength={MAX_REASON}
          placeholder="e.g. No search demand for these products"
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </div>

      <DialogFooter className="gap-2">
        <Button
          variant="outline"
          data-testid="post-step-accept-cancel"
          className="cursor-pointer"
          disabled={isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid="post-step-accept-confirm"
          className="cursor-pointer"
          isLoading={isPending}
          disabled={isPending || trimmed.length === 0}
          onClick={() => {
            accept(
              {
                step: target.step,
                reason: trimmed,
                ...(target.productIds ? { productIds: target.productIds } : {}),
                ...(target.productClass ? { productClass: target.productClass } : {}),
                ...(target.gaveUpOnly ? { gaveUpOnly: true } : {}),
              },
              { onSuccess: onClose },
            );
          }}
        >
          Accept as is
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
